import { FC } from "react";

import { PageLink } from "./PageLink";
import { SectionScoreDashboardDataType } from "./hooks";

import { PageType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { UserWithSectionUserType } from "store/selectors/types";
import { getUserFullName } from "utils/user-utils";

type PagePointTableProps = {
  publishedPagesWithAssessments: PageType[];
  studentPointsPerPageCSVData: SectionScoreDashboardDataType["studentPointsPerPageCSVData"];
  studentPointsPerPageTableRows: SectionScoreDashboardDataType["studentPointsPerPageTableRows"];
  sectionStudents: UserWithSectionUserType[];
};

export const PagePointTable: FC<PagePointTableProps> = ({
  publishedPagesWithAssessments,
  studentPointsPerPageCSVData,
  studentPointsPerPageTableRows,
  sectionStudents,
}) => {
  if (publishedPagesWithAssessments.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer
      exportData={studentPointsPerPageCSVData}
      title={t("assessment_dashboard.student_points_per_page_title")}
    >
      <Table>
        {/* Headers: Page / Page Type / Avg. Points / User A / User B / User C... */}
        {/* One row per page, showing each student's score on that page */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.assessment_category_header_tooltip")}>
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.avg_points_header_tooltip")}>
                {t("assessment_dashboard.all.avg_points_header_title")}
              </Tooltip>
            </TH>

            {sectionStudents.map((student) => (
              <TH key={student.user_id} verticalDivTitle={getUserFullName(student)} isVertical>
                {getUserFullName(student)}
              </TH>
            ))}
          </TR>
        </THead>

        <tbody>
          {studentPointsPerPageTableRows.map((row) => (
            <TR key={row.id}>
              <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.page.title}>
                <PageLink
                  className="w-full truncate text-blue-shade-20 underline"
                  page={row.page}
                />
              </TD>

              <TD>{row.pageType}</TD>

              <TD>{row.avgPoints}</TD>

              {row.studentScores.map((score, index) => (
                <TD key={sectionStudents[index].user_id}>{score}</TD>
              ))}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
